import { createContext, PropsWithChildren, useCallback, useEffect, useRef, useState } from 'react';

import FadeOverlay from 'components/FadeOverlay/FadeOverlay';
import SplashScreen from 'components/SplashScreen/SplashScreen';

type IntroRequest = { reason: string; onComplete: () => void };
/**
 * Optional progress readout rendered inside the branded splash while a reason
 * is holding it. Used by the cold-launch OTA gate so the download shows a bar
 * and a percent instead of an indefinite splash. Kept as a payload on the
 * existing gate rather than a second full-screen state so the SplashScreen
 * stays mounted continuously from boot into Lobby's own showSplash — a second
 * mount would restart the logo animation mid-boot.
 */
export type SplashDetail = { label: string; percent: number };
// 'full' is the branded SplashScreen (cold start); 'light' is the quicker
// FadeOverlay used for in-session navigation once the player has already
// seen the full splash this session (see each caller's own _launched-style
// module flag, which decides which variant to request).
type SplashVariant = 'full' | 'light';

type SplashScreenContextType = {
  showSplash: (reason: string, variant?: SplashVariant, detail?: SplashDetail) => void;
  hideSplash: (reason: string) => void;
  /**
   * Play the one-shot, self-dismissing branded intro (fade in → hold → fade
   * out) and call onComplete when it finishes. Unlike showSplash/hideSplash,
   * the caller doesn't decide when this hides — the animation does, then
   * reports back. Only ever one intro plays at a time; a second playIntro()
   * before the first completes replaces it.
   */
  playIntro: (reason: string, onComplete: () => void) => void;
  /** Cancel a pending/in-flight intro before it completes on its own — call
   * from the requesting effect's cleanup so an unmount doesn't fire a stale
   * onComplete. */
  cancelIntro: (reason: string) => void;
};

const SplashScreenContext = createContext<SplashScreenContextType>({
  showSplash: () => {},
  hideSplash: () => {},
  playIntro: () => {},
  cancelIntro: () => {}
});

/**
 * Single mount point for the full-screen branded SplashScreen AND its
 * lightweight sibling FadeOverlay. Callers don't render either themselves —
 * they either:
 *
 *  - call showSplash(reason, variant?)/hideSplash(reason) for a "block until
 *    ready" loading gate (native login's pre-reload window, Lobby's
 *    first-load-before-lobbyReady window, etc.) — reasons are tracked in a
 *    map (reason → variant), not a single boolean, so two unrelated callers
 *    each showing/hiding independently can't clobber each other's visibility
 *    (caller A's hideSplash() would otherwise hide the screen out from under
 *    caller B, which still wants it up). variant defaults to 'full'; pass
 *    'light' for the quicker FadeOverlay treatment (in-session navigation
 *    that's already shown the full splash once this session). If any active
 *    reason wants 'full', that wins over any 'light' reasons — the more
 *    prominent branded treatment always takes priority when both are
 *    requested at once.
 *
 *  - call playIntro(reason, onComplete) for the one-shot, self-dismissing
 *    branded intro (Game's cold-start "grand entrance" once currentTable
 *    arrives) — this is a fundamentally different shape: the animation
 *    decides when it's done, not the caller, so it reports back via
 *    onComplete instead of being hidden externally.
 *
 * A pending loading-gate reason always takes priority over the intro: if
 * anything still needs the screen blocked, the timed intro is deferred
 * (simply not rendered, so its GSAP timeline hasn't started yet) rather than
 * racing to reveal not-ready content underneath.
 */
type Request = { variant: SplashVariant; detail?: SplashDetail };

/**
 * How long a cleared reason keeps holding the splash, so the next reason can
 * take over without the screen coming down in between.
 *
 * One macrotask, not a duration anyone should tune. Boot hands the splash from
 * one gate to the next — the OTA boot gate to Lobby's own "not ready yet"
 * gate, native login to the lobby, a navigation to its destination's gate —
 * and a successor CANNOT register its reason until the render pass after the
 * predecessor's effect clears its own. Dropping the reason synchronously
 * therefore empties the map for one pass, which unmounts SplashScreen, flashes
 * whatever is underneath, and then mounts a fresh splash that replays the
 * whole logo intro with a new quip. On device that reads as the launch screen
 * appearing twice in a row, which is what players reported.
 *
 * setTimeout(…, 0) rather than an animation frame because a successor's
 * showSplash usually runs in a passive effect, which React can flush after
 * paint. App.tsx's 'pendingReturnPath' has been working around this at its own
 * call site for a while; doing it here means no caller has to know.
 */
const HANDOFF_MS = 0;

export const SplashScreenProvider = ({ children }: PropsWithChildren) => {
  const [reasons, setReasons] = useState<Map<string, Request>>(new Map());
  const [intro, setIntro] = useState<IntroRequest | null>(null);
  /** Reasons whose removal is scheduled but not applied yet (see HANDOFF_MS). */
  const pendingHides = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  const cancelPendingHide = useCallback((reason: string) => {
    const timer = pendingHides.current.get(reason);
    if (timer === undefined) return;
    clearTimeout(timer);
    pendingHides.current.delete(reason);
  }, []);

  // Drop every scheduled removal on unmount, so a timer cannot fire into a
  // provider that is gone.
  useEffect(
    () => () => {
      pendingHides.current.forEach(clearTimeout);
      pendingHides.current.clear();
    },
    []
  );

  const showSplash = useCallback(
    (reason: string, variant: SplashVariant = 'full', detail?: SplashDetail) => {
      // A reason shown again inside its own handoff window keeps its hold
      // rather than being removed a beat later by the pending timer.
      cancelPendingHide(reason);
      setReasons((prev) => {
        const current = prev.get(reason);
        if (
          current?.variant === variant &&
          current?.detail?.percent === detail?.percent &&
          current?.detail?.label === detail?.label
        ) {
          return prev;
        }
        const next = new Map(prev);
        next.set(reason, { variant, detail });
        return next;
      });
    },
    [cancelPendingHide]
  );

  const hideSplash = useCallback((reason: string) => {
    // Deferred, never immediate — see HANDOFF_MS. Already-pending hides are
    // left alone rather than rescheduled, so a caller that hides the same
    // reason repeatedly (an effect cleanup on every dependency change) cannot
    // push the removal further and further out.
    if (pendingHides.current.has(reason)) return;
    const timer = setTimeout(() => {
      pendingHides.current.delete(reason);
      setReasons((prev) => {
        if (!prev.has(reason)) return prev;
        const next = new Map(prev);
        next.delete(reason);
        return next;
      });
    }, HANDOFF_MS);
    pendingHides.current.set(reason, timer);
  }, []);

  const playIntro = useCallback((reason: string, onComplete: () => void) => {
    setIntro((prev) => (prev?.reason === reason ? prev : { reason, onComplete }));
  }, []);

  const cancelIntro = useCallback((reason: string) => {
    setIntro((prev) => (prev?.reason === reason ? null : prev));
  }, []);

  const isBlocked = reasons.size > 0;
  const requests = Array.from(reasons.values());
  const hasFullReason = requests.some((request) => request.variant === 'full');
  // Take the detail only from a request whose variant is the one actually
  // being rendered. Picking the first request with a detail regardless of
  // variant meant a 'light' request's progress could be drawn on the full
  // branded splash requested by something else entirely. Only the boot gate
  // sets a detail today, so this is not reachable yet, but the pairing should
  // hold by construction rather than by there being one caller.
  const detail = requests.find(
    (request) => request.detail && (hasFullReason ? request.variant === 'full' : true)
  )?.detail;
  // Read directly from render-scope state rather than memoizing: SplashScreen
  // only ever consults the onComplete it was given on its OWN first render
  // (its animation effect has an empty dependency array), so this only needs
  // to be correct at that one moment, not referentially stable across renders.
  const handleIntroComplete = () => {
    intro?.onComplete();
    setIntro(null);
  };

  return (
    <SplashScreenContext.Provider value={{ showSplash, hideSplash, playIntro, cancelIntro }}>
      {children}
      {isBlocked &&
        (hasFullReason ? (
          <SplashScreen persist detail={detail} onComplete={() => {}} />
        ) : (
          <FadeOverlay />
        ))}
      {!isBlocked && intro && <SplashScreen onComplete={handleIntroComplete} />}
    </SplashScreenContext.Provider>
  );
};

export default SplashScreenContext;
